import { useNotificationStore, type Notification } from '../stores/notificationStore';

const LEVEL_ACCENTS: Record<Notification['level'], { border: string; icon: string }> = {
  info: { border: '#7dd3fc', icon: 'ℹ' },
  warning: { border: '#fbbf24', icon: '!' },
  error: { border: '#dc2626', icon: '✕' },
};

/**
 * Renders the active notification stack in the bottom-right corner.
 * Lightweight: each card auto-dismisses per its TTL (handled in the store);
 * users can also click × or any action button to dismiss early.
 */
export function NotificationOverlay() {
  const notifications = useNotificationStore((s) => s.notifications);
  const dismiss = useNotificationStore((s) => s.dismiss);

  if (notifications.length === 0) return null;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '16px',
        right: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        zIndex: 9999,
        pointerEvents: 'none',
        maxWidth: '360px',
      }}
    >
      {notifications.map((n) => {
        const accent = LEVEL_ACCENTS[n.level];
        return (
          <div
            key={n.id}
            role={n.level === 'error' ? 'alert' : 'status'}
            style={{
              pointerEvents: 'auto',
              backgroundColor: 'var(--color-background-elevated, #1a1a20)',
              borderLeft: `3px solid ${accent.border}`,
              borderRadius: 'var(--radius-sm, 4px)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
              padding: '10px 12px',
              fontFamily: 'Georgia, serif',
              fontSize: '13px',
              color: 'var(--color-text-primary, #e5e5e5)',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px',
              animation: 'notifSlideIn 220ms ease-out',
            }}
          >
            <span
              aria-hidden="true"
              style={{ color: accent.border, fontWeight: 600, lineHeight: '18px' }}
            >
              {accent.icon}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 500, lineHeight: 1.3 }}>{n.message}</div>
              {n.detail && (
                <div style={{
                  marginTop: '4px',
                  fontSize: '11px',
                  color: 'var(--color-text-secondary, #999)',
                  lineHeight: 1.3,
                }}>
                  {n.detail}
                </div>
              )}
              {n.action && (
                <button
                  onClick={() => {
                    n.action?.onClick();
                    dismiss(n.id);
                  }}
                  style={{
                    marginTop: '6px',
                    background: 'transparent',
                    border: `1px solid ${accent.border}`,
                    color: accent.border,
                    padding: '3px 8px',
                    fontSize: '11px',
                    cursor: 'pointer',
                    borderRadius: 'var(--radius-sm, 4px)',
                    fontFamily: 'inherit',
                  }}
                >
                  {n.action.label}
                </button>
              )}
            </div>
            <button
              onClick={() => dismiss(n.id)}
              title="Dismiss"
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--color-text-secondary, #999)',
                cursor: 'pointer',
                fontSize: '14px',
                padding: '0 4px',
                lineHeight: 1,
              }}
            >
              ×
            </button>
          </div>
        );
      })}
      <style>{`
        @keyframes notifSlideIn {
          from { opacity: 0; transform: translateX(12px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}
